import { useMemo } from 'react';
import {
  Box, SimpleGrid, Icon, useColorModeValue, Text, Flex,
  Badge,
} from '@chakra-ui/react';
import {
  MdPeople, MdToday, MdAccessTime, MdCheckCircle,
  MdCalendarMonth, MdPerson, MdAdd, MdChevronRight,
} from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import MiniStatistics from 'components/card/MiniStatistics';
import IconBox from 'components/icons/IconBox';
import Card from 'components/card/Card';
import TodayJornada from 'components/layouts/dashboard/TodayJornada';
import { useAppointments } from 'hooks/useAppointments';
import { usePatients } from 'hooks/usePatients';
import { useCurrentDbUser } from 'hooks/useCurrentUser';
import { EstadoCita } from 'types/models';

function todayRange() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  const today = `${y}-${m}-${d}`;
  return { desde: today, hasta: today };
}

interface QuickActionProps {
  icon: any;
  label: string;
  description?: string;
  onClick: () => void;
  accent?: string;
}

function QuickAction({ icon, label, description, onClick, accent = 'brand.500' }: QuickActionProps) {
  const bg          = useColorModeValue('gray.50', 'whiteAlpha.50');
  const hoverBg     = useColorModeValue('brand.50', 'whiteAlpha.100');
  const textColor   = useColorModeValue('secondaryGray.900', 'white');
  const mutedColor  = useColorModeValue('gray.500', 'gray.400');
  const iconBg      = useColorModeValue('white', 'navy.700');

  return (
    <Flex
      align='center'
      gap='14px'
      p='14px'
      borderRadius='14px'
      bg={bg}
      _hover={{ bg: hoverBg, cursor: 'pointer', transform: 'translateX(3px)' }}
      transition='all 0.15s'
      onClick={onClick}
    >
      <Flex
        w='44px' h='44px' borderRadius='12px' bg={iconBg}
        align='center' justify='center' flexShrink={0}
        boxShadow='0 2px 6px rgba(0,0,0,0.08)'
      >
        <Icon as={icon} w='22px' h='22px' color={accent} />
      </Flex>
      <Box flex={1} minW={0}>
        <Text fontSize='md' fontWeight='700' color={textColor} noOfLines={1}>{label}</Text>
        {description && <Text fontSize='sm' color={mutedColor} noOfLines={1}>{description}</Text>}
      </Box>
      <Icon as={MdChevronRight} w='18px' h='18px' color={mutedColor} flexShrink={0} />
    </Flex>
  );
}

export default function FisioDashboard() {
  const brandColor  = useColorModeValue('brand.500', 'white');
  const boxBg       = useColorModeValue('secondaryGray.300', 'whiteAlpha.100');
  const textColor   = useColorModeValue('secondaryGray.900', 'white');
  const mutedColor  = useColorModeValue('gray.500', 'gray.400');
  const headerBg    = useColorModeValue('white', 'navy.800');
  const borderColor = useColorModeValue('gray.100', 'whiteAlpha.100');

  const navigate     = useNavigate();
  const { user }     = useUser();
  const { data: me } = useCurrentDbUser();

  const nombre = user?.firstName ?? 'Profesional';
  const range  = useMemo(todayRange, []);
  const { data: patients } = usePatients();
  const totalPacientes = patients?.length ?? null;

  const dateLabel = new Date().toLocaleDateString('es-EC', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });

  const { data: todayData } = useAppointments(
    me?.id ? { desde: range.desde, hasta: range.hasta, professionalId: me.id, limit: 50 } : {}
  );

  const citasHoy        = todayData?.data?.length ?? 0;
  const citasPendientes = todayData?.data?.filter(a => a.estado === EstadoCita.CONFIRMADA).length ?? 0;
  const citasCompletadas = todayData?.data?.filter(a => a.estado === EstadoCita.COMPLETADA).length ?? 0;

  const stat = (n: number) => citasHoy > 0 ? String(n) : '—';

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>

      {/* ── Greeting ─────────────────────────────────────────────────── */}
      <Flex
        align='center' justify='space-between' mb='24px'
        p='20px' borderRadius='16px' bg={headerBg}
        border='1px solid' borderColor={borderColor}
        boxShadow='0 1px 4px rgba(0,0,0,0.04)'
        flexWrap='wrap' gap='12px'
      >
        <Box>
          <Text color={textColor} fontSize='xl' fontWeight='700' mb='2px'>
            Hola, {nombre} 👋
          </Text>
          <Text color={mutedColor} fontSize='sm' textTransform='capitalize'>
            {dateLabel}
          </Text>
        </Box>
        {citasPendientes > 0 && (
          <Badge colorScheme='orange' borderRadius='full' px='14px' py='6px' fontSize='sm'>
            {citasPendientes} cita{citasPendientes !== 1 ? 's' : ''} pendiente{citasPendientes !== 1 ? 's' : ''}
          </Badge>
        )}
      </Flex>

      {/* ── Stats ────────────────────────────────────────────────────── */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap='20px' mb='24px'>
        <MiniStatistics
          startContent={
            <IconBox w='56px' h='56px' bg={boxBg}
              icon={<Icon w='32px' h='32px' as={MdToday} color={brandColor} />} />
          }
          name='Citas Hoy'
          value={stat(citasHoy)}
        />
        <MiniStatistics
          startContent={
            <IconBox w='56px' h='56px' bg={boxBg}
              icon={<Icon w='32px' h='32px' as={MdAccessTime} color='orange.400' />} />
          }
          name='Pendientes'
          value={stat(citasPendientes)}
        />
        <MiniStatistics
          startContent={
            <IconBox w='56px' h='56px' bg={boxBg}
              icon={<Icon w='32px' h='32px' as={MdCheckCircle} color='green.400' />} />
          }
          name='Completadas Hoy'
          value={stat(citasCompletadas)}
        />
        <MiniStatistics
          startContent={
            <IconBox w='56px' h='56px' bg='linear-gradient(90deg, #4481EB 0%, #04BEFE 100%)'
              icon={<Icon w='28px' h='28px' as={MdPeople} color='white' />} />
          }
          name='Total Pacientes'
          value={totalPacientes !== null ? String(totalPacientes) : '—'}
        />
      </SimpleGrid>

      {/* ── Main content: Jornada + Quick Actions ────────────────────── */}
      <SimpleGrid columns={{ base: 1, xl: 2 }} gap='20px' alignItems='start'
        gridTemplateColumns={{ xl: '1fr 300px' }}>

        {/* Jornada del día */}
        <TodayJornada title='Mi Jornada de Hoy' />

        {/* Quick Actions */}
        <Card p='20px'>
          <Text color={textColor} fontSize='md' fontWeight='700' mb='4px'>
            Acciones Rápidas
          </Text>
          <Text color={mutedColor} fontSize='xs' mb='16px'>Atajos frecuentes</Text>
          <Flex direction='column' gap='8px'>
            <QuickAction
              icon={MdAdd}
              label='Agendar Cita'
              description='Nueva cita para paciente'
              onClick={() => navigate('/admin/agenda')}
              accent='brand.500'
            />
            <QuickAction
              icon={MdPeople}
              label='Ver Pacientes'
              description='Listado de pacientes'
              onClick={() => navigate('/admin/patients')}
              accent='blue.400'
            />
            <QuickAction
              icon={MdCalendarMonth}
              label='Ir a Agenda'
              description='Vista completa del calendario'
              onClick={() => navigate('/admin/agenda')}
              accent='teal.400'
            />
            <QuickAction
              icon={MdPerson}
              label='Mi Perfil'
              description='Configuración y disponibilidad'
              onClick={() => navigate('/admin/profile')}
              accent='purple.400'
            />
          </Flex>
        </Card>
      </SimpleGrid>
    </Box>
  );
}
